// CustomerDetail — one customer + all their bank accounts.
//
// Route: /customers/:customerId. Viewer+ at the membership layer.
//
// Renders:
//   - Customer header: name, email, phone, created_at
//   - Bank accounts table: every Beneficiary row linked to this
//     customer. Click a row → /beneficiaries/:beneficiaryId for the
//     existing detail page (verification status, payouts history, etc.)
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Pill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { getCustomer } from "@/api/customers";
import { useUI } from "@/store/ui";

export function CustomerDetail() {
  const navigate = useNavigate();
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const { customerId: cidParam } = useParams<{ customerId: string }>();

  const customerId =
    cidParam != null && /^\d+$/.test(cidParam) ? Number(cidParam) : null;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["customer", currentMerchantId, customerId],
    queryFn: () =>
      currentMerchantId == null || customerId == null
        ? Promise.reject(new Error("missing merchant or customer id"))
        : getCustomer(currentMerchantId, customerId),
    enabled: currentMerchantId != null && customerId != null,
  });

  if (customerId == null) {
    return (
      <div className="page">
        <Link to="/customers" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to customers
        </Link>
        <h1 style={{ marginTop: 16 }}>Customer</h1>
        <div className="card flat">
          <div className="empty">
            <p>Invalid customer id in the URL.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page">
        <Link to="/customers" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to customers
        </Link>
        <div className="empty" style={{ marginTop: 24 }}>
          <div className="dim">Loading customer…</div>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="page">
        <Link to="/customers" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to customers
        </Link>
        <h1 style={{ marginTop: 16 }}>Customer not found</h1>
        <p className="dim">
          No customer with id {customerId}, or it was removed.
          {error instanceof Error ? ` (${error.message})` : ""}
        </p>
      </div>
    );
  }

  const { customer, beneficiaries } = data;

  return (
    <div className="page">
      <Link to="/customers" className="btn ghost sm">
        <I.ChevronLeft size={12} /> Back to customers
      </Link>

      <div className="page-head" style={{ marginTop: 12 }}>
        <div>
          <div className="eyebrow">cus_{customer.id}</div>
          <h1>{customer.name || <span className="dim">(no name)</span>}</h1>
          <div className="sub">
            Added {formatDateTime(customer.created_at)} ·{" "}
            {customer.beneficiary_count}{" "}
            {customer.beneficiary_count === 1 ? "bank account" : "bank accounts"}
          </div>
        </div>
      </div>

      <div className="card flat" style={{ marginTop: 12 }}>
        <div className="card-head">
          <h3>Contact</h3>
        </div>
        <div
          style={{
            padding: "0 16px 16px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          <InfoCell label="Email" value={customer.email} mono />
          <InfoCell label="Phone" value={customer.phone} mono />
        </div>
      </div>

      <div className="card flat" style={{ marginTop: 12 }}>
        <div className="card-head">
          <h3>Bank accounts</h3>
          <div className="spacer" />
          <span className="dim" style={{ fontSize: 11 }}>
            {beneficiaries.length}{" "}
            {beneficiaries.length === 1 ? "account" : "accounts"}
          </span>
        </div>
        {beneficiaries.length === 0 ? (
          <div className="empty">
            <p>
              No bank accounts registered for this customer yet. Add one via
              the Beneficiaries page or upload a CSV that references this
              customer's email and phone.
            </p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Account</th>
                <th>IFSC</th>
                <th>Registered</th>
                <th>Verified</th>
                <th>Added</th>
              </tr>
            </thead>
            <tbody>
              {beneficiaries.map((b) => (
                <tr
                  key={b.id}
                  className="clickable"
                  onClick={() => navigate(`/beneficiaries/${b.id}`)}
                >
                  <td>
                    <div className="cell-name">{b.name}</div>
                    <div className="cell-id mono">{b.account_no}</div>
                  </td>
                  <td className="mono dim" style={{ fontSize: 12 }}>
                    {b.ifsc}
                  </td>
                  <td>
                    {b.is_registered ? (
                      <Pill tone="pos">Registered</Pill>
                    ) : (
                      <Pill tone="warn">Pending</Pill>
                    )}
                  </td>
                  <td>
                    {b.verified_at ? (
                      <Pill tone="pos">Verified</Pill>
                    ) : (
                      <Pill tone="">Not verified</Pill>
                    )}
                  </td>
                  <td className="dim" style={{ fontSize: 12 }}>
                    {formatDateTime(b.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function InfoCell({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div>
      <div
        className="eyebrow"
        style={{ fontSize: 10, letterSpacing: "0.06em", marginBottom: 4 }}
      >
        {label}
      </div>
      <div className={mono ? "mono" : undefined} style={{ fontSize: 14 }}>
        {value}
      </div>
    </div>
  );
}
